import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { useOpenById } from "../lib/useOpenById";
import { WindingSvg } from "../components/WindingPicker";
import { ArticleFormFields, EMPTY_ARTICLE } from "../components/ArticleForm";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

export default function Articles() {
  const { t, canWrite } = useApp();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [accountId, setAccountId] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const debounce = useRef(null);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setQDeb(q); setPage(1); }, 400);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page, page_size: 50 });
    if (qDeb) params.set("q", qDeb);
    if (accountId) params.set("account_id", accountId);
    api.get(`/articles?${params}`).then((r) => setData(r.data)).catch(() => {});
  }, [page, qDeb, accountId]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);
  useOpenById("articles", setEditing);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/articles/${editing.id}`, editing);
      else await api.post("/articles", editing);
      toast.success(t("save") + " ✓");
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const remove = async () => {
    try {
      const r = await api.delete(`/articles/${editing.id}`);
      toast.success(r.data.deactivated ? t("art_deactivated") : t("delete") + " ✓");
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div className="space-y-4" data-testid="articles-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("article_master")}</h1>
        {canWrite && (
          <Button data-testid="articles-new-btn" onClick={() => setEditing({ ...EMPTY_ARTICLE })}
            className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
            <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("art_new")}
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input data-testid="articles-list-search" className="pl-8 h-9" placeholder={t("search_placeholder")}
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="articles-filter-account" className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700 max-w-[220px]"
            value={accountId} onChange={(e) => { setAccountId(e.target.value); setPage(1); }}>
            <option value="">{`${t("ord_customer")}: ${t("all")}`}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <span className="text-xs text-gray-500 font-mono">{data.total} {t("records")}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-2.5 font-semibold">{t("article_number")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("art_name")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("ord_customer")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("art_material")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("art_size")}</th>
                <th className="px-4 py-2.5 font-semibold text-center">{t("art_winding")}</th>
                <th className="px-4 py-2.5 font-semibold text-right">{t("art_price")}</th>
                <th className="px-4 py-2.5 font-semibold">{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id} data-testid="articles-row" onClick={() => canWrite && setEditing(a)}
                  className="border-b border-gray-100 hover:bg-amber-50/40 cursor-pointer transition-colors duration-150">
                  <td className="px-4 py-2.5 font-mono font-medium text-[#1B2A4A]">{a.article_number}</td>
                  <td className="px-4 py-2.5">{a.name}</td>
                  <td className="px-4 py-2.5 text-gray-500">{a.account_name}</td>
                  <td className="px-4 py-2.5 text-xs">{a.material}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{a.width_mm}×{a.height_mm}</td>
                  <td className="px-4 py-2.5">
                    <div className="w-10 mx-auto"><WindingSvg dir={Number(a.winding_direction) || 1} className="w-full h-7" /></div>
                    <div className="text-[10px] text-center text-gray-500 font-semibold">{a.winding_direction}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmtEur(a.price_per_1000)}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={a.status === "Active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-gray-100 text-gray-500 hover:bg-gray-100"}>{a.status}</Badge>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">{t("empty_title")}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
          <div className="flex-1" />
          <span className="text-xs text-gray-500">{t("ord_page")} {data.page || page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="articles-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="articles-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? `${t("edit")} – ${editing.article_number}` : t("art_new")}</DialogTitle>
            <DialogDescription>{t("article_master")}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <ArticleFormFields editing={editing} set={set} accounts={accounts} />
              <div className="flex justify-between pt-2">
                {editing.id ? (
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove} data-testid="article-delete-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />{t("delete")}
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditing(null)}>{t("cancel")}</Button>
                  <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={save} data-testid="article-save-btn">{t("save")}</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
